/**
 * DashboardCountryVaults — combined Map & Vaults dashboard panel
 *
 * Split screen:
 *   LEFT  — wallet stats, policy legend, quick links (320px, collapses on fullscreen)
 *   RIGHT — Mapbox globe using countries.geojson with:
 *             • same light-v11 style, no fog/stars (matches main map exactly)
 *             • same policy coloring green / red / gray
 *             • NavigationControl + GeolocateControl (same as main map)
 *             • debounced search box (Mapbox geocoding)
 *             • hover tooltip showing allowed status + live vault balance
 *             • click → CountryManagementOverlay (admin) or CountryProfileOverlay
 *             • fullscreen toggle
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import turfCircle from '@turf/circle';
import 'mapbox-gl/dist/mapbox-gl.css';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';
import { iso2ToNumeric, iso3ToIso2 } from '../countryCodes';
import { COUNTRY_LIST, PIE_COLORS } from '../v2/countryList';
import { CountryProfileOverlay }    from '../CountryProfileOverlay';
import { CountryManagementOverlay } from '../CountryManagementOverlay';
import PasskeyKitPanel              from './PasskeyKitPanel';
import {
  ensureCountryOverlayLayers,
  syncBasemapAdminBoundaries,
  syncCountryBorderSource,
} from '../utils/countryOverlayStyle';

// ── Constants ─────────────────────────────────────────────────────────────────

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const XLM_SAC =
  process.env.REACT_APP_NATIVE_XLM_SAC_ADDRESS ||
  'CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── Types ─────────────────────────────────────────────────────────────────────

interface HoverState { code: number; name: string; iso2: string; x: number; y: number; }
interface TooltipVault {
  iso2:            string;
  balance:         string | null;  // G-address vault balance (stroops)
  passkeyBalance:  string | null;  // Linked passkey smart-account vault balance (stroops)
  totalBalance:    string | null;  // All-users vault total (stroops)
  loading:         boolean;
  allowed:         boolean | null; // policy allowed/denied
  vaultEnabled:    boolean | null; // on-chain vault enabled flag
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  walletAddress:   string | null;
  wallet:          any;
  passkeyKitService: any;
  contractClient:  any;
  readOnlyClient:  any;
  mainAdminAddress: string | null;
  isAdmin:         boolean;
  allowedCountries: Set<number>;
  defaultAllowAll?: boolean;
  totalCountries?: number;
  stellarBalance?: string | null;
  allowedCount?:  number;
  onDepositSuccess?: () => void;
  onWalletBalanceChange?: () => void;
  onNavigate?:    (section: string) => void;
  onPolicyChanged?: () => void;
  onCountryAllowedChange?: (countryCode: number, allowed: boolean) => void;
  // Markers (same data as main map)
  playerLocation?:  [number, number] | null;
  otherUsers?:      Array<{ id: string; location: [number, number]; distance: number }>;
  nearbyNFTs?:      any[];
  nearbyContracts?: any[];
  /** Current map style from the settings panel — kept in sync with the main map */
  mapStyle?: string;
  /** Incremented when admin saves border changes; triggers a re-fetch of countries.geojson */
  borderRevision?: number;
  /** Called after admin saves border changes — lets App.tsx refresh the main map */
  onBordersChanged?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const DashboardCountryVaults: React.FC<Props> = ({
  walletAddress,
  wallet,
  passkeyKitService,
  contractClient,
  readOnlyClient,
  mainAdminAddress,
  isAdmin,
  allowedCountries,
  defaultAllowAll = false,
  totalCountries = 203,
  stellarBalance,
  allowedCount,
  onDepositSuccess,
  onWalletBalanceChange,
  onNavigate,
  onPolicyChanged,
  onCountryAllowedChange,
  playerLocation,
  otherUsers = [],
  nearbyNFTs = [],
  nearbyContracts = [],
  mapStyle = 'mapbox://styles/mapbox/light-v11',
  borderRevision = 0,
  onBordersChanged,
}) => {
  // ── Map refs ───────────────────────────────────────────────────────────────
  const mapContainer     = useRef<HTMLDivElement>(null);
  const map              = useRef<mapboxgl.Map | null>(null);
  const geojsonRef       = useRef<any>(null);
  const activeMapStyleRef = useRef<string | null>(null); // tracks the style the map was initialised/last-set with
  const hoverRef      = useRef<HoverState | null>(null);
  const tooltipOnRef          = useRef(false);
  const leaveTimer            = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentFetchingIso2   = useRef<string | null>(null);

  // Marker refs (same as main map)
  const playerMarkerRef       = useRef<mapboxgl.Marker | null>(null);
  const nftMarkersRef         = useRef<mapboxgl.Marker[]>([]);
  const contractMarkersRef    = useRef<mapboxgl.Marker[]>([]);
  const otherUserMarkersRef   = useRef<mapboxgl.Marker[]>([]);
  // Contract layer refs — polygon fills (geofence) + hover circles (location/proximity)
  const contractLayerSourcesRef = useRef<string[]>([]);
  const hoverCircleRef          = useRef<{ sourceId: string; layerId: string } | null>(null);

  // Keep wallet/admin refs fresh inside event closures
  const walletRef    = useRef(walletAddress);
  const adminRef     = useRef(mainAdminAddress);
  const roClientRef  = useRef(readOnlyClient);
  useEffect(() => { walletRef.current   = walletAddress;    }, [walletAddress]);
  useEffect(() => { adminRef.current    = mainAdminAddress; }, [mainAdminAddress]);
  useEffect(() => { roClientRef.current = readOnlyClient;   }, [readOnlyClient]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [mapReady,   setMapReady]   = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 768);

  const [selectedCountry, setSelectedCountry] = useState<{ code: number; name: string } | null>(null);
  const [showProfile,     setShowProfile]     = useState(false);
  const [showAdmin,       setShowAdmin]       = useState(false);

  const [hover,        setHover]        = useState<HoverState | null>(null);
  const [tooltipVault, setTooltipVault] = useState<TooltipVault | null>(null);

  const [searchQ,       setSearchQ]       = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFocus,   setSearchFocus]   = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Selected marker popup (NFT / contract / user)
  const [selectedDashMarker, setSelectedDashMarker] = useState<{
    type: 'nft' | 'contract' | 'user'; lngLat: [number, number]; data: any;
  } | null>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Country vault positions ────────────────────────────────────────────────

  interface CountryPosition {
    iso2: string; name: string;
    walletBal: string | null; passkeyBal: string | null;
  }

  const [countryPositions,   setCountryPositions]   = useState<CountryPosition[]>([]);
  const [positionsLoading,   setPositionsLoading]   = useState(false);
  const [showAllPositions,   setShowAllPositions]   = useState(false);

  // Prevents concurrent scans (e.g. when walletAddress + passkeyKitService both change).
  const scanningRef = useRef(false);

  const loadCountryPositions = useCallback(async () => {
    if (!walletAddress || scanningRef.current) return;
    scanningRef.current = true;
    setPositionsLoading(true);

    const linkedAccount = (passkeyKitService as any)?.getAccountByWallet?.(walletAddress) ?? null;

    // Scope the scan to allowed countries when the set is already populated.
    // Fall back to all countries when it's still loading (size = 0) so we never
    // show an empty table due to a loading race.
    //   defaultAllowAll = false → allowedCountries = codes that ARE allowed
    //   defaultAllowAll = true  → allowedCountries = codes that ARE denied
    let scanList = COUNTRY_LIST;
    if (!defaultAllowAll && allowedCountries.size > 0) {
      const filtered = COUNTRY_LIST.filter(({ iso2 }) => {
        const code = iso2ToNumeric(iso2.toUpperCase());
        return code != null && allowedCountries.has(code);
      });
      if (filtered.length > 0) scanList = filtered; // only apply if non-empty
    } else if (defaultAllowAll && allowedCountries.size > 0) {
      const filtered = COUNTRY_LIST.filter(({ iso2 }) => {
        const code = iso2ToNumeric(iso2.toUpperCase());
        return code == null || !allowedCountries.has(code);
      });
      if (filtered.length > 0) scanList = filtered;
    }

    // Individual GET requests — browser ETag caching returns 304 on repeat calls,
    // making subsequent scans near-instant even for a large scan list.
    const fetchBal = async (iso2: string, addr: string): Promise<string | null> => {
      try {
        const url = new URL(`${BACKEND_URL}/api/v2/vault/balance`, window.location.origin);
        url.searchParams.set('userAddress',  addr);
        url.searchParams.set('country',      iso2);
        url.searchParams.set('assetAddress', XLM_SAC);
        const r = await fetch(url.toString());
        if (!r.ok) return null;
        const d = await r.json();
        return d.balance && d.balance !== '0' ? d.balance : null;
      } catch { return null; }
    };

    const results: CountryPosition[] = [];
    const BATCH = 10;
    for (let i = 0; i < scanList.length; i += BATCH) {
      const batch = scanList.slice(i, i + BATCH);
      const batchRes = await Promise.allSettled(batch.map(async ({ iso2, name }) => {
        const [wb, pb] = await Promise.all([
          fetchBal(iso2, walletAddress),
          linkedAccount ? fetchBal(iso2, linkedAccount.accountAddress) : Promise.resolve(null),
        ]);
        return { iso2, name, walletBal: wb, passkeyBal: pb };
      }));
      for (const r of batchRes) {
        if (r.status === 'fulfilled' && (r.value.walletBal || r.value.passkeyBal)) {
          results.push(r.value);
        }
      }
      if (results.length > 0) setCountryPositions([...results]); // progressive update
    }

    setCountryPositions(
      results.sort((a, b) => {
        const av = BigInt(a.walletBal || '0') + BigInt(a.passkeyBal || '0');
        const bv = BigInt(b.walletBal || '0') + BigInt(b.passkeyBal || '0');
        return bv > av ? 1 : bv < av ? -1 : 0;
      }),
    );

    setPositionsLoading(false);
    scanningRef.current = false;
  }, [walletAddress, passkeyKitService, defaultAllowAll, allowedCountries]);

  // Only auto-scan once when walletAddress first becomes available.
  const didAutoScanRef = useRef(false);
  useEffect(() => {
    if (walletAddress && !didAutoScanRef.current) {
      didAutoScanRef.current = true;
      loadCountryPositions();
    }
    if (!walletAddress) didAutoScanRef.current = false;
  }, [walletAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a vault deposit: refresh parent's wallet balance AND re-scan positions.
  const handleDepositSuccess = useCallback(() => {
    onDepositSuccess?.();
    // Allow a new scan (reset the guard) then load immediately.
    scanningRef.current = false;
    loadCountryPositions();
  }, [onDepositSuccess, loadCountryPositions]);

  // Fly the map to a country given its ISO-2 code.
  // Uses the same lookup strategy as the tooltip's "Fly to" button — numeric code
  // first, then country name — because geojsonRef stores the raw GeoJSON (before
  // enrichment) which does not have an ISO2 field.
  const flyToCountryIso2 = useCallback((iso2: string) => {
    if (!map.current || !geojsonRef.current) return;

    const upper        = iso2.toUpperCase();
    const numericCode  = iso2ToNumeric(upper) ?? null;
    const countryName  = (COUNTRY_LIST.find(c => c.iso2 === upper)?.name ?? '').toLowerCase();

    const feat = geojsonRef.current.features?.find((f: any) => {
      const p    = f.properties || {};
      const fNum = typeof p.ISO_NUMERIC === 'number' ? p.ISO_NUMERIC : parseInt(p.ISO_NUMERIC, 10);
      if (numericCode !== null && !isNaN(fNum) && fNum === numericCode) return true;
      if (countryName && (p.name || '').toLowerCase() === countryName) return true;
      // Fallback: match via ISO3 id (e.g. GBR → GB) for features with missing ISO_NUMERIC/name
      if (iso3ToIso2(String(f.id || '')).toUpperCase() === upper) return true;
      return false;
    });

    if (!feat) return;

    const coords = feat.geometry?.coordinates?.flat?.(Infinity);
    if (!coords || coords.length < 2) return;
    const lngs = coords.filter((_: any, i: number) => i % 2 === 0);
    const lats = coords.filter((_: any, i: number) => i % 2 !== 0);
    const cLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    const cLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    map.current.flyTo({ center: [cLng, cLat], zoom: 4, speed: 1.4 });
  }, []);

  const countryPieData = countryPositions.map(p => ({
    name: p.iso2,
    value: Number(BigInt(p.walletBal || '0') + BigInt(p.passkeyBal || '0')) / 10_000_000,
  })).filter(p => p.value > 0);

  const totalCountryVaultXlm = countryPieData.reduce((s, d) => s + d.value, 0);

  // ── Policy helpers ─────────────────────────────────────────────────────────

  const isAllowed = useCallback((code: number): boolean | null => {
    if (allowedCountries.size === 0) return null;
    return defaultAllowAll ? !allowedCountries.has(code) : allowedCountries.has(code);
  }, [allowedCountries, defaultAllowAll]);

  const buildEnrichedGeojson = useCallback((raw: any) => {
    if (!raw?.features) return raw;
    const features = raw.features.map((f: any) => {
      const p    = f.properties || {};
      const iso3 = String(f.id || '');
      // Resolve ISO2 — always write it back so the mousemove handler can read it
      const i2   = p.ISO2 || p.iso2 || iso3ToIso2(iso3) || '';
      let code: number | undefined = p.ISO_NUMERIC;
      if (!code && i2) code = iso2ToNumeric(i2) ?? undefined;
      const allowed = code !== undefined ? isAllowed(code) : undefined;
      return { ...f, properties: { ...p, ISO_NUMERIC: code, ISO2: i2, allowed } };
    });
    return { ...raw, features };
  }, [isAllowed]);

  // ── Tooltip vault balance ─────────────────────────────────────────────────

  const loadTooltipVault = useCallback(async (iso2: string, code: number) => {
    // Mark this iso2 as the active fetch; stale checks below will abort superseded calls
    currentFetchingIso2.current = iso2;

    const allowed = isAllowed(code);

    // Show loading state immediately — no balance data yet, no vault status yet
    setTooltipVault({ iso2, balance: null, passkeyBalance: null, totalBalance: null, loading: true, allowed, vaultEnabled: null });

    // ── Step 1: on-chain vault info (with 3-second timeout, mirroring main map) ──
    let vaultEnabled: boolean | null = null;
    if (readOnlyClient && iso2) {
      try {
        const timeout = new Promise<null>(res => setTimeout(() => res(null), 3000));
        const info = await Promise.race([readOnlyClient.getCountryInfo(iso2), timeout]);
        // null → vault not registered (or timeout/RPC error), false → registered but disabled
        vaultEnabled = info != null ? !!info.enabled : null;
      } catch { vaultEnabled = null; }
    }

    // Stale check — user may have moved to another country while we awaited
    if (currentFetchingIso2.current !== iso2) return;

    // Progressive update 1: vault status is known; balances still loading
    setTooltipVault(prev =>
      prev?.iso2 === iso2
        ? { ...prev, vaultEnabled, loading: true }
        : prev
    );

    // ── Step 2: balances — only when vault is registered and enabled ──
    if (!walletAddress || vaultEnabled !== true) {
      // No need to fetch balances; close out
      if (currentFetchingIso2.current !== iso2) return;
      setTooltipVault(prev =>
        prev?.iso2 === iso2
          ? { ...prev, loading: false }
          : prev
      );
      return;
    }

    // Helper: fetch stroops balance for one address
    const fetchBal = async (addr: string): Promise<string | null> => {
      try {
        const url = new URL(`${BACKEND_URL}/api/v2/vault/balance`, window.location.origin);
        url.searchParams.set('userAddress',  addr);
        url.searchParams.set('country',      iso2);
        url.searchParams.set('assetAddress', XLM_SAC);
        const r = await fetch(url.toString());
        if (!r.ok) return null;
        const d = await r.json();
        return d.balance && d.balance !== '0' ? d.balance : null;
      } catch { return null; }
    };

    // Identify passkey smart-account linked to this wallet
    const linkedAccount = (passkeyKitService as any)?.getAccountByWallet?.(walletAddress) ?? null;

    // Fetch user balances + vault total in parallel
    const [walletBal, passkeyBal, totalBalance] = await Promise.all([
      fetchBal(walletAddress),
      linkedAccount ? fetchBal(linkedAccount.accountAddress) : Promise.resolve(null),
      (async (): Promise<string | null> => {
        try {
          const totUrl = new URL(`${BACKEND_URL}/api/v2/vault/total-balance`, window.location.origin);
          totUrl.searchParams.set('country',      iso2);
          totUrl.searchParams.set('assetAddress', XLM_SAC);
          const r = await fetch(totUrl.toString());
          if (!r.ok) return null;
          const d = await r.json();
          return d.totalBalance ?? null;
        } catch { return null; }
      })(),
    ]);

    // Stale check after balance fetches
    if (currentFetchingIso2.current !== iso2) return;

    // Progressive update 2: all data ready
    setTooltipVault(prev =>
      prev?.iso2 === iso2
        ? { ...prev, balance: walletBal, passkeyBalance: passkeyBal, totalBalance, loading: false }
        : prev
    );
  }, [walletAddress, isAllowed, readOnlyClient, passkeyKitService]);

  // ── Search ─────────────────────────────────────────────────────────────────

  const handleSearch = (q: string) => {
    setSearchQ(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&limit=5&types=country,region,place`
        );
        const d = await r.json();
        setSearchResults(d.features || []);
      } catch { /* ignore */ }
      finally { setSearchLoading(false); }
    }, 300);
  };

  const flyToResult = (feature: any) => {
    if (!map.current) return;
    const [lng, lat] = feature.center;
    const zoom = feature.place_type?.[0] === 'country' ? 4 : 10;
    map.current.flyTo({ center: [lng, lat], zoom, speed: 1.4 });
    setSearchQ(''); setSearchResults([]); setSearchFocus(false);
  };

  // ── Map init ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!MAPBOX_TOKEN || !mapContainer.current || map.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const resolvedStyle = mapStyle && mapStyle.startsWith('mapbox://')
      ? mapStyle
      : `mapbox://styles/mapbox/${mapStyle || 'light-v11'}`;

    activeMapStyleRef.current = resolvedStyle; // record so the mapStyle effect skips redundant setStyle calls

    const m = new mapboxgl.Map({
      container: mapContainer.current,
      style:     resolvedStyle,
      center:    [0, 20],
      zoom:      1.4,
      projection: 'globe' as any,
      attributionControl: false,
      // Match main map MSAA so thin policy outlines anti-alias the same way.
      antialias: true,
    });

    // Same controls as main map
    m.addControl(new mapboxgl.NavigationControl({
      showCompass: true, showZoom: true, visualizePitch: true,
    }), 'top-left');

    const geoCtrl = new mapboxgl.GeolocateControl({
      positionOptions:   { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading:   true,
    });
    m.addControl(geoCtrl, 'top-left');

    // ── Player location marker (same as main map) ───────────────────
    let playerMarker: mapboxgl.Marker | null = null;
    geoCtrl.on('geolocate', (pos: any) => {
      const { longitude, latitude } = pos.coords;
      const el = document.createElement('div');
      el.style.cssText = [
        'width:18px', 'height:18px', 'border-radius:50%',
        'background:linear-gradient(135deg,#6366f1,#8b5cf6)',
        'border:3px solid #fff',
        'box-shadow:0 0 0 3px rgba(99,102,241,0.4)',
        'cursor:pointer',
      ].join(';');
      if (playerMarker) playerMarker.remove();
      playerMarker = new mapboxgl.Marker({ element: el })
        .setLngLat([longitude, latitude])
        .addTo(m);
    });

    m.once('load', async () => {
      // NO setFog — avoids stars/atmosphere; matches main map behavior
      try {
        const resp = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
        if (!resp.ok) return;
        const raw = await resp.json();
        geojsonRef.current = raw;
        const enriched = buildEnrichedGeojson(raw);

        m.addSource('countries', { type: 'geojson', data: enriched });
        syncCountryBorderSource(m, enriched);
        // Transparent fill (hit-test) + unique-edge policy-coloured outline.
        ensureCountryOverlayLayers(m);

        // ── Mouse events ─────────────────────────────────────────────

        m.on('mousemove', 'countries-fill', (e: any) => {
          const f = e.features?.[0];
          if (!f) return;
          const p    = f.properties;
          const code = typeof p?.ISO_NUMERIC === 'number' ? p.ISO_NUMERIC : parseInt(p?.ISO_NUMERIC, 10);
          const name = p?.name || p?.NAME || `Country ${code}`;
          const iso2 = p?.ISO2 || p?.iso2 || '';
          if (!code || isNaN(code)) return;

          m.getCanvas().style.cursor = 'pointer';

          if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }

          const prev = hoverRef.current;
          const isNewCountry = !prev || prev.code !== code;

          // Lock the tooltip position when first entering a country — do NOT update
          // x/y on every mousemove so the tooltip stays still and the user can reach it.
          const x = isNewCountry ? e.point.x : prev!.x;
          const y = isNewCountry ? e.point.y : prev!.y;

          const next: HoverState = { code, name, iso2, x, y };
          hoverRef.current = next;

          // Only trigger a React re-render (and vault fetch) when the country changes.
          if (isNewCountry) {
            setHover(next);
            loadTooltipVault(iso2, code);
          }
        });

        m.on('mouseleave', 'countries-fill', () => {
          m.getCanvas().style.cursor = '';
          // Match main-map 1 000 ms grace period so the user can reach the tooltip.
          if (!tooltipOnRef.current) {
            leaveTimer.current = setTimeout(() => {
              if (!tooltipOnRef.current) {
                setHover(null); setTooltipVault(null); hoverRef.current = null;
              }
            }, 1000);
          }
        });

        // ── Click: always open CountryProfileOverlay first (same as main map) ──
        // Admin actions (Manage Country) are inside CountryProfileOverlay via onManageCountry

        m.on('click', 'countries-fill', (e: any) => {
          const f = e.features?.[0];
          if (!f) return;
          const p    = f.properties;
          const name = p?.name || p?.NAME || '';
          const code = typeof p?.ISO_NUMERIC === 'number' ? p.ISO_NUMERIC : parseInt(p?.ISO_NUMERIC, 10);
          if (!code || isNaN(code)) return;
          setSelectedCountry({ code, name });
          setShowProfile(true);
        });

        // Show basemap country borders under the coloured overlay; hide provinces.
        syncBasemapAdminBoundaries(m);

        setMapReady(true);
      } catch (err) {
      }
    });

    map.current = m;
    return () => { map.current?.remove(); map.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-color when policy changes ───────────────────────────────────────────

  useEffect(() => {
    if (!map.current || !mapReady || !geojsonRef.current) return;
    if (!map.current.isStyleLoaded()) return;
    try {
      const enriched = buildEnrichedGeojson(geojsonRef.current);
      (map.current.getSource('countries') as mapboxgl.GeoJSONSource)?.setData(enriched);
      syncCountryBorderSource(map.current, enriched);
      ensureCountryOverlayLayers(map.current);
    } catch (_) {}
  }, [allowedCountries, defaultAllowAll, mapReady, buildEnrichedGeojson]);

  // ── Re-fetch geometry when admin saves border changes ──────────────────────
  // borderRevision increments each time borders are saved via CountryManagementOverlay.
  // Skip revision 0 (initial mount) — the map.once('load') handler covers that case.
  useEffect(() => {
    if (borderRevision === 0 || !map.current || !mapReady) return;
    if (!map.current.isStyleLoaded()) return;
    (async () => {
      try {
        const resp = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
        if (!resp.ok) return;
        const raw = await resp.json();
        geojsonRef.current = raw;
        const enriched = buildEnrichedGeojson(raw);
        (map.current!.getSource('countries') as mapboxgl.GeoJSONSource)?.setData(enriched);
        syncCountryBorderSource(map.current!, enriched);
        ensureCountryOverlayLayers(map.current!);
      } catch (_) {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borderRevision]);

  // Called when admin saves borders from within the dashboard's CountryManagementOverlay.
  // Re-fetches the dashboard map's own source AND notifies App.tsx (which refreshes the main map).
  const handleDashboardBordersChanged = useCallback(async () => {
    // Re-fetch and update the dashboard map
    if (map.current && mapReady && map.current.isStyleLoaded()) {
      try {
        const resp = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
        if (resp.ok) {
          const raw = await resp.json();
          geojsonRef.current = raw;
          const enriched = buildEnrichedGeojson(raw);
          (map.current!.getSource('countries') as mapboxgl.GeoJSONSource)?.setData(enriched);
          syncCountryBorderSource(map.current!, enriched);
          ensureCountryOverlayLayers(map.current!);
        }
      } catch (_) {}
    }
    // Bubble up so App.tsx can refresh the main map
    if (onBordersChanged) onBordersChanged();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, onBordersChanged, buildEnrichedGeojson]);

  // Resize map on fullscreen toggle
  useEffect(() => { setTimeout(() => map.current?.resize(), 60); }, [fullscreen]);

  // Keep dashboard map style in sync with the main map style picker.
  // Only call setStyle when the style genuinely changes — calling it with the
  // same URL would tear down and restart the map for no reason, wiping layers.
  useEffect(() => {
    if (!map.current || !mapStyle) return;
    const resolved = mapStyle.startsWith('mapbox://')
      ? mapStyle
      : `mapbox://styles/mapbox/${mapStyle}`;
    if (resolved === activeMapStyleRef.current) return; // same style — nothing to do
    activeMapStyleRef.current = resolved;
    try {
      map.current.setStyle(resolved);
      // Re-add country layers once the new style settles. Prefer `idle` —
      // diff-style updates may skip `style.load` (same bug as the main map).
      const reattach = () => {
        if (!map.current || !geojsonRef.current) return;
        try {
          const enriched = buildEnrichedGeojson(geojsonRef.current);
          if (!map.current.getSource('countries')) {
            map.current.addSource('countries', { type: 'geojson', data: enriched });
          } else {
            (map.current.getSource('countries') as mapboxgl.GeoJSONSource).setData(enriched);
          }
          syncCountryBorderSource(map.current, enriched);
          ensureCountryOverlayLayers(map.current);
          syncBasemapAdminBoundaries(map.current);
        } catch (_) { /* style mid-swap */ }
      };
      map.current.once('style.load', reattach);
      map.current.once('idle', reattach);
    } catch { /* map not ready */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  // ── Player location marker ─────────────────────────────────────────────────
  useEffect(() => {
    if (!map.current || !mapReady) return;
    if (playerMarkerRef.current) { playerMarkerRef.current.remove(); playerMarkerRef.current = null; }
    if (!playerLocation) return;
    const el = document.createElement('div');
    el.className = 'marker-player';
    el.style.cssText = `
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      background: #FFD700;
      box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      cursor: pointer;
      position: relative;
    `;
    playerMarkerRef.current = new mapboxgl.Marker({ element: el })
      .setLngLat(playerLocation)
      .addTo(map.current);
  }, [playerLocation, mapReady]);

  // ── Other-user markers ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!map.current || !mapReady) return;
    otherUserMarkersRef.current.forEach(m => m.remove());
    otherUserMarkersRef.current = [];
    otherUsers.forEach(user => {
      if (!map.current) return;
      const el = document.createElement('div');
      el.className = 'marker marker-opponent other-user-marker';
      el.innerHTML = `<div class="marker-distance">${user.distance.toFixed(1)} km</div>`;
      el.style.cssText = 'cursor:pointer;display:flex;align-items:center;justify-content:center;position:absolute;width:24px;height:24px;transform:translate(-50%,-50%);pointer-events:auto;';
      let lastClick = 0; let singleTimer: any = null;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const now = Date.now(); const delta = now - lastClick; lastClick = now;
        if (delta < 300) {
          clearTimeout(singleTimer); singleTimer = null; lastClick = 0;
          map.current?.flyTo({ center: user.location, zoom: Math.max(14, (map.current?.getZoom() ?? 0) + 2), speed: 1.4 });
        } else {
          singleTimer = setTimeout(() => {
            singleTimer = null;
            setSelectedDashMarker({ type: 'user', lngLat: user.location, data: user });
          }, 300);
        }
      });
      el.addEventListener('dblclick', e => { e.stopPropagation(); e.preventDefault(); });
      try {
        const m = new mapboxgl.Marker({ element: el }).setLngLat(user.location).addTo(map.current!);
        otherUserMarkersRef.current.push(m);
      } catch { /* ignore */ }
    });
  }, [otherUsers, mapReady]);

  // ── NFT markers ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!map.current || !mapReady) return;
    nftMarkersRef.current.forEach(m => m.remove());
    nftMarkersRef.current = [];
    nearbyNFTs.forEach((nft: any) => {
      if (!nft.latitude || !nft.longitude || !map.current) return;

      // Construct image URL — exact same priority order as main map's constructImageUrl:
      //   1. Build from server_url + ipfs_hash (strips + re-adds https:// to normalise)
      //   2. Fall back to image_url
      //   3. Final fallback placeholder
      const buildImageUrl = (serverUrl: string | null | undefined, ipfsHash: string | null | undefined): string => {
        if (ipfsHash) {
          let base = serverUrl ? serverUrl.trim() : '';
          base = base.replace(/\/ipfs\/.*$/i, '').replace(/\/+$/, '').replace(/^https?:\/\//i, '');
          return base ? `https://${base}/ipfs/${ipfsHash}` : `https://ipfs.io/ipfs/${ipfsHash}`;
        }
        return '';
      };
      const imageUrl =
        buildImageUrl(nft.server_url, nft.ipfs_hash) ||
        nft.image_url ||
        'https://via.placeholder.com/64x64?text=NFT';

      const el = document.createElement('div');
      el.className = 'nft-marker';
      const zoom = map.current.getZoom();
      const sz = zoom >= 14 ? 64 : zoom >= 10 ? 40 : 32;
      el.style.cssText = `width:${sz}px;height:${sz}px;background-image:url('${imageUrl}');background-size:cover;background-repeat:no-repeat;background-position:center;border-radius:8px;border:2px solid #FFD700;cursor:pointer;box-shadow:0 2px 8px rgba(255,215,0,0.5);display:flex;align-items:center;justify-content:center;position:absolute;transform:translate(-50%,-50%);pointer-events:auto;transition:width .2s,height .2s;`;
      el.title = nft.collection_name || nft.name || 'NFT';
      // Use new Image() to probe the URL — CSS background-image failures do NOT fire DOM error events
      const probe = new Image();
      probe.onerror = () => {
        el.style.backgroundImage = 'url(https://via.placeholder.com/64x64?text=NFT)';
        el.style.backgroundSize = 'cover';
      };
      probe.src = imageUrl;

      // Single / double-click — same pattern as main map
      let lastClick = 0; let singleTimer: any = null;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const now = Date.now(); const delta = now - lastClick; lastClick = now;
        if (delta < 300) {
          clearTimeout(singleTimer); singleTimer = null; lastClick = 0;
          map.current?.flyTo({ center: [nft.longitude, nft.latitude], zoom: Math.max(15, (map.current?.getZoom() ?? 0) + 2), speed: 1.4 });
        } else {
          singleTimer = setTimeout(() => {
            singleTimer = null;
            setSelectedDashMarker({ type: 'nft', lngLat: [nft.longitude, nft.latitude], data: nft });
          }, 300);
        }
      });
      el.addEventListener('dblclick', e => { e.stopPropagation(); e.preventDefault(); });

      try {
        const m = new mapboxgl.Marker({ element: el }).setLngLat([nft.longitude, nft.latitude]).addTo(map.current!);
        nftMarkersRef.current.push(m);
      } catch { /* ignore */ }
    });
  }, [nearbyNFTs, mapReady]);

  // ── Contract markers (type-aware: polygon for geofence, hover circle for location/proximity) ──
  useEffect(() => {
    if (!map.current || !mapReady) return;
    const m = map.current;

    // Remove old markers
    contractMarkersRef.current.forEach(mk => mk.remove());
    contractMarkersRef.current = [];

    // Remove old polygon/circle layers
    if (m.isStyleLoaded()) {
      contractLayerSourcesRef.current.forEach(sourceId => {
        [`${sourceId}-fill`, `${sourceId}-border`, `${sourceId}-layer`].forEach(lid => {
          if (m.getLayer(lid)) m.removeLayer(lid);
        });
        if (m.getSource(sourceId)) m.removeSource(sourceId);
      });
    }
    contractLayerSourcesRef.current = [];

    const DASH_GRADIENTS: Record<string, string> = {
      location:  'linear-gradient(135deg,#7B68EE 0%,#9d8fff 100%)',
      proximity: 'linear-gradient(135deg,#4A90D9 0%,#6aaef0 100%)',
      geofence:  'linear-gradient(135deg,#FF6B35 0%,#ff8c60 100%)',
    };
    const DASH_BORDERS: Record<string, string> = {
      location:  '#b8adff',
      proximity: '#7bb9f5',
      geofence:  '#ffaa87',
    };
    const DASH_SHADOWS: Record<string, string> = {
      location:  'rgba(123,104,238,0.5)',
      proximity: 'rgba(74,144,217,0.5)',
      geofence:  'rgba(255,107,53,0.5)',
    };
    const DASH_COLOURS: Record<string, string> = {
      location:  '#7B68EE',
      proximity: '#4A90D9',
      geofence:  '#FF6B35',
    };

    // Helper: make a GeoJSON circle as a true geodesic buffer (@turf/circle).
    // The previous equirectangular dx/dy approximation skewed at latitude.
    const makeCircleGeo = (cLng: number, cLat: number, radiusM: number, pts = 64) => {
      const feature = turfCircle([cLng, cLat], radiusM / 1000, {
        steps: pts,
        units: 'kilometers',
      });
      return { type: 'FeatureCollection' as const, features: [feature] };
    };

    // Hover circle helpers (location / proximity only)
    const showHoverCircle = (cLng: number, cLat: number, radiusM: number, colour: string) => {
      if (!m.isStyleLoaded()) return;
      // Clear previous
      if (hoverCircleRef.current) {
        const { sourceId: sid, layerId: lid } = hoverCircleRef.current;
        if (m.getLayer(lid))  m.removeLayer(lid);
        if (m.getSource(sid)) m.removeSource(sid);
        hoverCircleRef.current = null;
      }
      const sid = `dash-hover-circle-${Date.now()}`;
      const lid = `${sid}-layer`;
      try {
        m.addSource(sid, { type: 'geojson', data: makeCircleGeo(cLng, cLat, radiusM) as any });
        m.addLayer({ id: lid, type: 'fill', source: sid, paint: { 'fill-color': colour, 'fill-opacity': 0.18 } });
        m.addLayer({ id: `${lid}-line`, type: 'line', source: sid,
          paint: { 'line-color': colour, 'line-width': 2, 'line-dasharray': [3, 2] } });
        hoverCircleRef.current = { sourceId: sid, layerId: lid };
      } catch { /* style not ready */ }
    };

    const hideHoverCircle = () => {
      if (!hoverCircleRef.current || !m.isStyleLoaded()) return;
      const { sourceId: sid, layerId: lid } = hoverCircleRef.current;
      [`${lid}-line`, lid].forEach(l => { if (m.getLayer(l)) m.removeLayer(l); });
      if (m.getSource(sid)) m.removeSource(sid);
      hoverCircleRef.current = null;
    };

    nearbyContracts.forEach((contract: any, idx: number) => {
      const lat = parseFloat(contract.latitude);
      const lng = parseFloat(contract.longitude);
      if (isNaN(lat) || isNaN(lng) || !map.current) return;

      const ruleType  = contract.rule_type || 'location';
      const colour    = DASH_COLOURS[ruleType]   || '#7B68EE';
      const bg        = DASH_GRADIENTS[ruleType] || DASH_GRADIENTS.location;
      const border    = DASH_BORDERS[ruleType]   || DASH_BORDERS.location;
      const shadow    = DASH_SHADOWS[ruleType]   || DASH_SHADOWS.location;
      const boundaryGeo = contract.boundary_geojson as { type: string; coordinates: any[] } | null | undefined;

      // ── Geofence: persistent filled polygon ──────────────────────────
      if (ruleType === 'geofence' && boundaryGeo?.coordinates && m.isStyleLoaded()) {
        const sourceId = `dash-poly-${contract.id}-${idx}`;
        try {
          if (!m.getSource(sourceId)) {
            m.addSource(sourceId, {
              type: 'geojson',
              data: { type: 'Feature', geometry: boundaryGeo as any, properties: {} },
            });
            m.addLayer({ id: `${sourceId}-fill`,   type: 'fill', source: sourceId,
              paint: { 'fill-color': colour, 'fill-opacity': 0.12 } });
            m.addLayer({ id: `${sourceId}-border`, type: 'line', source: sourceId,
              paint: { 'line-color': colour, 'line-width': 2, 'line-opacity': 0.85 } });
            contractLayerSourcesRef.current.push(sourceId);
          }
        } catch { /* style not ready */ }
      }

      // ── Marker pin ───────────────────────────────────────────────────
      const el = document.createElement('div');
      el.className = 'contract-marker';
      const zoom = m.getZoom();
      const sz = zoom >= 14 ? 64 : zoom >= 10 ? 40 : 32;
      const fs = sz >= 64 ? 32 : sz >= 40 ? 22 : 16;
      el.style.cssText = `width:${sz}px;height:${sz}px;background:${bg};border-radius:6px;border:2px solid ${border};cursor:pointer;box-shadow:0 2px 8px ${shadow};display:flex;align-items:center;justify-content:center;font-size:${fs}px;color:white;position:absolute;transform:translate(-50%,-50%);pointer-events:auto;transition:width .2s,height .2s,font-size .2s;`;
      el.innerHTML = '🧮';
      el.title = `${contract.contract_name || contract.name || 'Smart Contract'}${contract.auto_execute ? ' (Auto-execute)' : ''}`;
      if (contract.auto_execute) { el.style.border = '2px solid #FFD700'; el.style.boxShadow = '0 2px 8px rgba(255,215,0,0.7)'; }

      let lastClick = 0; let singleTimer: any = null;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const now = Date.now(); const delta = now - lastClick; lastClick = now;
        if (delta < 300) {
          clearTimeout(singleTimer); singleTimer = null; lastClick = 0;
          map.current?.flyTo({ center: [lng, lat], zoom: Math.max(15, (map.current?.getZoom() ?? 0) + 2), speed: 1.4 });
        } else {
          singleTimer = setTimeout(() => {
            singleTimer = null;
            setSelectedDashMarker({ type: 'contract', lngLat: [lng, lat], data: contract });
          }, 300);
        }
      });
      el.addEventListener('dblclick', e => { e.stopPropagation(); e.preventDefault(); });

      // Hover circle only for location / proximity (geofence already shows polygon)
      if (ruleType !== 'geofence') {
        const r = contract.radius_meters || 5000;
        el.addEventListener('mouseenter', () => showHoverCircle(lng, lat, r, colour));
        el.addEventListener('mouseleave', hideHoverCircle);
      }

      try {
        const mk = new mapboxgl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map.current!);
        contractMarkersRef.current.push(mk);
      } catch { /* ignore */ }
    });
  }, [nearbyContracts, mapReady]);

  // ── Tooltip helpers ────────────────────────────────────────────────────────

  // Accent color — exact same logic as main map
  const accentColor = tooltipVault?.loading
    ? '#4a9eff'
    : tooltipVault?.allowed === true  ? '#4CAF50'
    : tooltipVault?.allowed === false ? '#ef4444'
    : '#475569';

  const getTooltipStyle = (): React.CSSProperties => {
    if (!hover || !mapContainer.current) return { display: 'none' };
    const W    = mapContainer.current.offsetWidth;
    const H    = mapContainer.current.offsetHeight;
    const tipW = 292;
    const left = Math.min(hover.x + 20, W - tipW - 8);
    const top  = Math.max(8, Math.min(hover.y - 20, H - 380));
    return { left, top };
  };

  const fmt   = (s: string | null | undefined) => (Number(s ?? '0') / 10_000_000).toFixed(2);
  const trunc = (a: string | null)              => a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '—';

  const openVault = () => {
    if (!hover) return;
    setSelectedCountry({ code: hover.code, name: hover.name });
    setShowProfile(true);
  };

  // nav button helper (exact style from main map)
  const navBtn = (icon: string, title: string, onClick: () => void, highlight?: boolean) => (
    <button
      key={icon + title}
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        width: isMobile ? 40 : 30, height: isMobile ? 40 : 30,
        borderRadius: 8, border: 'none',
        background: highlight ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.09)',
        color: highlight ? '#818cf8' : '#94a3b8',
        fontSize: isMobile ? 16 : 14,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </button>
  );

  // Vault info block — exact same logic as main map (wallet+passkey combined)
  const vaultBlock = hover && (
    <div style={{ padding: isMobile ? '10px 16px' : '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', minHeight: 42 }}>
      {!walletAddress ? (
        <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>Connect wallet to see your balance</div>
      ) : !hover.iso2 ? (
        <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>Country code unavailable</div>
      ) : tooltipVault?.loading ? (
        <div style={{ fontSize: 12, color: '#4a9eff', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#4a9eff', animation: 'pulse 1s ease-in-out infinite' }} />
          Checking vault…
        </div>
      ) : tooltipVault?.vaultEnabled === false ? (
        // Vault is registered but admin disabled it
        <div style={{ fontSize: 12, color: '#ffa500' }}>● Vault registered but disabled</div>
      ) : (tooltipVault?.vaultEnabled === true && (tooltipVault.balance !== null || tooltipVault.passkeyBalance !== null)) ? (
        // Vault enabled + user has a balance — show wallet/passkey breakdown like main map
        (() => {
          const wb = Number(tooltipVault.balance ?? '0');
          const sb = Number(tooltipVault.passkeyBalance ?? '0');
          const combined = wb + sb;
          const hasBoth = tooltipVault.passkeyBalance !== null && wb > 0;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                {hasBoth && (
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>
                    Wallet {fmt(String(wb))} · Passkey {fmt(String(sb))}
                  </div>
                )}
                <div style={{ fontSize: isMobile ? 15 : 13, fontWeight: 700, color: '#e2e8f0' }}>
                  Your: {fmt(String(hasBoth ? combined : (wb || sb)))} XLM
                </div>
                <div style={{ fontSize: 10, color: '#4CAF50', fontWeight: 600, marginTop: 2 }}>
                  ● Deposits enabled
                </div>
              </div>
              {tooltipVault.totalBalance && (
                <div style={{ textAlign: 'right' as const }}>
                  <div style={{ fontSize: 10, color: '#64748b' }}>Vault total</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#4CAF50' }}>{fmt(tooltipVault.totalBalance)} XLM</div>
                </div>
              )}
            </div>
          );
        })()
      ) : tooltipVault?.vaultEnabled === true ? (
        // Vault enabled but user has no balance yet
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: isMobile ? 14 : 12, color: '#94a3b8' }}>No balance in this vault</div>
            <div style={{ fontSize: 10, color: '#4CAF50', fontWeight: 600, marginTop: 2 }}>● Deposits enabled</div>
          </div>
          {tooltipVault.totalBalance && (
            <div style={{ textAlign: 'right' as const }}>
              <div style={{ fontSize: 10, color: '#64748b' }}>Vault total</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#4CAF50' }}>{fmt(tooltipVault.totalBalance)} XLM</div>
            </div>
          )}
        </div>
      ) : tooltipVault?.allowed === false ? (
        <div style={{ fontSize: 12, color: '#ef4444' }}>● Restricted</div>
      ) : (
        <div style={{ fontSize: 12, color: '#475569' }}>○ No vault registered</div>
      )}
    </div>
  );

  // Search block integrated into tooltip (same as main map)
  const searchBlock = (
    <div style={{ padding: isMobile ? '10px 16px' : '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 14, color: '#475569', flexShrink: 0 }}>🔍</span>
        <input
          value={searchQ}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search any place…"
          style={{
            flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 7, color: '#e2e8f0',
            padding: isMobile ? '8px 10px' : '5px 8px',
            fontSize: isMobile ? 14 : 12, outline: 'none',
          }}
        />
        {searchLoading && <span style={{ fontSize: 11, color: '#4a9eff', flexShrink: 0 }}>…</span>}
      </div>
      {searchResults.length > 0 && (
        <div style={{
          position: isMobile ? 'static' : 'absolute',
          top: isMobile ? undefined : '100%',
          left: isMobile ? undefined : 10,
          right: isMobile ? undefined : 10,
          marginTop: isMobile ? 8 : undefined,
          zIndex: 10002, background: '#0e1623', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 8, overflowY: 'auto', maxHeight: 160,
          boxShadow: '0 8px 28px rgba(0,0,0,0.75)',
        }}>
          {searchResults.map((f: any, i: number) => (
            <div key={i} onMouseDown={() => flyToResult(f)} onClick={() => flyToResult(f)}
              style={{
                padding: isMobile ? '11px 14px' : '7px 11px',
                fontSize: isMobile ? 13 : 11, color: '#cbd5e1', cursor: 'pointer',
                borderBottom: i < searchResults.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                display: 'flex', gap: 8, alignItems: 'center',
              }}>
              <span style={{ color: '#475569', fontSize: 12, flexShrink: 0 }}>
                {f.place_type?.[0] === 'country' ? '🌍' : f.place_type?.[0] === 'region' ? '📍' : '🏙'}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{f.place_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Nav row — same buttons as main map desktop tooltip
  const navRow = hover && (
    <div style={{ padding: isMobile ? '10px 16px 14px' : '8px 10px', display: 'flex', gap: isMobile ? 7 : 6, alignItems: 'center', flexWrap: 'wrap' as const }}>
      {navBtn('🎯', `Fly to ${hover.name}`, () => {
        if (map.current && hover) {
          map.current.flyTo({ center: [0, 20], zoom: 2, speed: 1.2 }); // default fallback
          // Try to find the country center from geojson
          const feat = geojsonRef.current?.features?.find((f: any) => {
            const p = f.properties || {};
            return p.ISO_NUMERIC === hover.code || (p.name || '').toLowerCase() === hover.name.toLowerCase();
          });
          if (feat) {
            // Compute bounding box center
            const coords = feat.geometry?.coordinates?.flat?.(Infinity);
            if (coords && coords.length >= 2) {
              const lngs = coords.filter((_: any, i: number) => i % 2 === 0);
              const lats = coords.filter((_: any, i: number) => i % 2 !== 0);
              const cLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
              const cLat = (Math.min(...lats) + Math.max(...lats)) / 2;
              map.current.flyTo({ center: [cLng, cLat], zoom: 4, speed: 1.4 });
            }
          }
        }
      })}
      {navBtn('🏦', 'Open country vault', openVault, tooltipVault?.allowed === true)}
      {navBtn('+', 'Zoom in', () => map.current?.zoomIn())}
      {navBtn('−', 'Zoom out', () => map.current?.zoomOut())}
      {navBtn('🌍', 'World view', () => map.current?.flyTo({ center: [0, 20], zoom: 2, pitch: 0, bearing: 0, speed: 1.2 }))}
      {navBtn('📍', 'My location', () => navigator.geolocation?.getCurrentPosition(({ coords }) =>
        map.current?.flyTo({ center: [coords.longitude, coords.latitude], zoom: 12, speed: 1.4 })))}
      {navBtn('⛶', 'Reset pitch', () => map.current?.easeTo({ pitch: 0, bearing: 0, duration: 600 }))}
    </div>
  );

  // ── Rendered JSX pieces ────────────────────────────────────────────────────

  const mapPanel = (
    <div style={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden' }}>

      {/* Search hint (top bar — shows when no hover; collapses when tooltip has search) */}
      {!hover && (
        <div style={{
          position: 'absolute', top: 10, left: 10, right: 54, zIndex: 20,
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,.96)', borderRadius: 8,
            border: '1.5px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.10)',
            padding: '6px 10px',
          }}>
            <span style={{ fontSize: 14, color: '#9ca3af' }}>🔍</span>
            <input
              type="text"
              placeholder="Search countries, places…"
              value={searchQ}
              onChange={e => handleSearch(e.target.value)}
              onFocus={() => setSearchFocus(true)}
              onBlur={() => setTimeout(() => setSearchFocus(false), 200)}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent', color: '#1a1f2e' }}
            />
            {searchLoading && <span style={{ fontSize: 11, color: '#9ca3af' }}>…</span>}
            {searchQ && (
              <button onClick={() => { setSearchQ(''); setSearchResults([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: 0 }}>
                ✕
              </button>
            )}
          </div>
          {searchFocus && searchResults.length > 0 && (
            <div style={{
              background: '#fff', border: '1.5px solid #e5e7eb', borderTop: 'none',
              borderRadius: '0 0 8px 8px', boxShadow: '0 4px 12px rgba(0,0,0,.12)',
              maxHeight: 200, overflowY: 'auto',
            }}>
              {searchResults.map((r: any, i: number) => (
                <button key={i} onClick={() => flyToResult(r)} style={{
                  width: '100%', textAlign: 'left', padding: '8px 12px',
                  border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: 13, color: '#1a1f2e', borderBottom: '1px solid #f3f4f6',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span>{r.place_type?.[0] === 'country' ? '🌍' : '📍'}</span>
                  <span>{r.place_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fullscreen toggle */}
      <button onClick={() => setFullscreen(f => !f)} title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        style={{
          position: 'absolute', top: 10, right: 10, zIndex: 20,
          width: 36, height: 36, borderRadius: 8, border: '1.5px solid #e5e7eb',
          background: 'rgba(255,255,255,.96)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, boxShadow: '0 2px 6px rgba(0,0,0,.10)',
        }}>
        {fullscreen ? '⊡' : '⛶'}
      </button>

      {/* World-view reset */}
      <button
        onClick={() => map.current?.flyTo({ center: [0, 20], zoom: 1.4, pitch: 0, bearing: 0, speed: 1.2 })}
        title="World view"
        style={{
          position: 'absolute', bottom: 110, right: 10, zIndex: 20,
          width: 36, height: 36, borderRadius: 8, border: '1.5px solid #e5e7eb',
          background: 'rgba(255,255,255,.96)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, boxShadow: '0 2px 6px rgba(0,0,0,.10)',
        }}>
        🌍
      </button>

      {/* Map canvas */}
      <div ref={mapContainer} style={{ position: 'absolute', inset: 0 }} />

      {/* Policy stats + legend overlay (bottom-left corner of map) */}
      <div style={{
        position: 'absolute', bottom: 10, left: 10, zIndex: 20,
        background: 'rgba(255,255,255,0.93)',
        borderRadius: 8, padding: '7px 10px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,.12)',
        backdropFilter: 'blur(4px)',
        fontSize: 11,
        pointerEvents: 'none',
      }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 5, fontWeight: 700 }}>
          <span style={{ color: '#16a34a' }}>
            {allowedCount ?? (defaultAllowAll ? Math.max(0, totalCountries - allowedCountries.size) : allowedCountries.size)} Allowed
          </span>
          <span style={{ color: '#dc2626' }}>
            {defaultAllowAll ? allowedCountries.size : Math.max(0, totalCountries - (allowedCount ?? allowedCountries.size))} Restricted
          </span>
        </div>
        {[
          { color: '#16a34a', label: 'Allowed',    border: '#00FF00' },
          { color: '#dc2626', label: 'Restricted', border: '#FF0000' },
          { color: '#9ca3af', label: 'No data',    border: '#888888' },
        ].map(({ color, label, border }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
            <div style={{ width: 12, height: 8, borderRadius: 2, background: color + '22', border: `2px solid ${border}`, flexShrink: 0 }} />
            <span style={{ color: '#4b5563' }}>{label}</span>
          </div>
        ))}
      </div>

      {!MAPBOX_TOKEN && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', color: '#6b7280', gap: 8,
        }}>
          <span style={{ fontSize: 36 }}>🗺</span>
          <span style={{ fontSize: 13 }}>REACT_APP_MAPBOX_TOKEN not configured</span>
        </div>
      )}

      {/* ── Marker detail popup (NFT / contract / user) ────────────── */}
      {selectedDashMarker && (
        <div style={{
          position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10001, width: 300, maxWidth: '90vw',
          background: 'rgba(8,13,24,0.97)', color: '#fff',
          borderRadius: 12, fontFamily: 'system-ui,-apple-system,sans-serif',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)', backdropFilter: 'blur(16px)',
          border: `1px solid rgba(255,255,255,0.12)`,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {selectedDashMarker.type === 'nft'      ? `🖼 ${selectedDashMarker.data.collection_name || selectedDashMarker.data.name || 'NFT'}`
               : selectedDashMarker.type === 'contract' ? `🧮 ${selectedDashMarker.data.contract_name || selectedDashMarker.data.name || 'Smart Contract'}`
               : `👤 Nearby User`}
            </span>
            <button onClick={() => setSelectedDashMarker(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 26, height: 26, color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
          <div style={{ padding: '10px 14px 12px', fontSize: 12, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: 5 }}>
            {selectedDashMarker.type === 'nft' && selectedDashMarker.data.image_url && (
              <img src={selectedDashMarker.data.image_url} alt="NFT" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8, marginBottom: 4 }} />
            )}
            {selectedDashMarker.type === 'contract' && (
              <>
                {selectedDashMarker.data.contract_address && <div><span style={{ color: '#64748b' }}>Address:</span> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{selectedDashMarker.data.contract_address.slice(0,14)}…</span></div>}
                {selectedDashMarker.data.function_name  && <div><span style={{ color: '#64748b' }}>Function:</span> {selectedDashMarker.data.function_name}</div>}
                {selectedDashMarker.data.rule_name      && <div><span style={{ color: '#64748b' }}>Rule:</span> {selectedDashMarker.data.rule_name}</div>}
                {selectedDashMarker.data.radius_meters  && <div><span style={{ color: '#64748b' }}>Radius:</span> {(selectedDashMarker.data.radius_meters / 1000).toFixed(1)} km</div>}
                {selectedDashMarker.data.auto_execute   && <div style={{ color: '#ffd700', fontSize: 11 }}>⚡ Auto-execute enabled</div>}
              </>
            )}
            {selectedDashMarker.type === 'user' && (
              <>
                <div><span style={{ color: '#64748b' }}>Distance:</span> {selectedDashMarker.data.distance?.toFixed(1)} km</div>
                {selectedDashMarker.data.publicKey && <div><span style={{ color: '#64748b' }}>Key:</span> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{String(selectedDashMarker.data.publicKey).slice(0,10)}…</span></div>}
              </>
            )}
            <button
              onClick={() => { map.current?.flyTo({ center: selectedDashMarker.lngLat, zoom: 15, speed: 1.4 }); setSelectedDashMarker(null); }}
              style={{ marginTop: 4, padding: '7px 12px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 7, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
            >
              🎯 Fly to location
            </button>
          </div>
        </div>
      )}

      {/* ── Hover tooltip — exact same as main map ─────────────────── */}
      {hover && (() => {
        const headerSection = (
          <div style={{ padding: isMobile ? '10px 14px' : '10px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              {!isMobile && <div style={{ width: 10, height: 10, borderRadius: '50%', background: accentColor, boxShadow: `0 0 7px ${accentColor}`, flexShrink: 0 }} />}
              {hover.iso2 && (
                <img src={`https://flagcdn.com/w20/${hover.iso2.toLowerCase()}.png`} alt={hover.iso2}
                  style={{ width: isMobile ? 22 : 20, height: 'auto', borderRadius: 2, flexShrink: 0, display: 'block' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
              <span style={{ fontWeight: 700, fontSize: isMobile ? 16 : 14, color: '#f0f4ff', lineHeight: 1.2 }}>{hover.name}</span>
              {hover.iso2 && (
                <span style={{ fontSize: isMobile ? 11 : 10, fontWeight: 700, color: '#94a3b8', background: 'rgba(255,255,255,0.1)', borderRadius: isMobile ? 5 : 4, padding: isMobile ? '2px 7px' : '1px 5px', letterSpacing: '0.06em' }}>
                  {hover.iso2}
                </span>
              )}
            </div>
            {isMobile
              ? <button onClick={() => { setHover(null); setTooltipVault(null); hoverRef.current = null; }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 30, height: 30, color: '#94a3b8', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              : <div style={{ width: 8, height: 8, borderRadius: '50%', background: accentColor, boxShadow: `0 0 6px ${accentColor}`, flexShrink: 0 }} />
            }
          </div>
        );

        if (isMobile) {
          return (
            <div onClick={e => e.stopPropagation()} style={{
              position: 'absolute', top: 76, left: 8, right: 8,
              margin: '0 auto', maxWidth: 360,
              zIndex: 9999,
              background: '#0b1120',
              borderRadius: 14,
              border: `2px solid ${accentColor}`,
              boxShadow: '0 10px 36px rgba(0,0,0,0.65)',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              maxHeight: '58vh', overflowY: 'auto',
            }}>
              {headerSection}
              {vaultBlock}
              <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <button onClick={openVault} style={{
                  width: '100%', padding: 12, borderRadius: 10, border: 'none',
                  background: tooltipVault?.allowed ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.08)',
                  color: tooltipVault?.allowed ? '#fff' : '#64748b',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  boxShadow: tooltipVault?.allowed ? '0 4px 16px rgba(99,102,241,0.4)' : 'none',
                }}>
                  {tooltipVault?.allowed ? '🏦 Open Vault' : '🏦 View Country'}
                </button>
              </div>
              {searchBlock}
              {navRow}
            </div>
          );
        }

        return (
          <div
            onMouseEnter={() => { tooltipOnRef.current = true; if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; } }}
            onMouseLeave={() => {
              tooltipOnRef.current = false;
              leaveTimer.current = setTimeout(() => {
                if (!tooltipOnRef.current) { setHover(null); setTooltipVault(null); hoverRef.current = null; }
              }, 200);
            }}
            style={{
              position: 'absolute', ...getTooltipStyle(), width: 292,
              zIndex: 10000, pointerEvents: 'auto',
              backgroundColor: 'rgba(8, 13, 24, 0.97)', color: '#fff',
              borderRadius: 12, fontSize: 13,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
              backdropFilter: 'blur(16px)',
              borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: 'rgba(255,255,255,0.1)',
              borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: 'rgba(255,255,255,0.1)',
              borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: 'rgba(255,255,255,0.1)',
              borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: accentColor,
              overflow: 'visible',
            }}
          >
            {headerSection}
            {vaultBlock}
            {searchBlock}
            {navRow}
          </div>
        );
      })()}
    </div>
  );

  const leftPanel = (
    <div style={{
      flex: fullscreen ? '0 0 0' : (isMobile ? '0 0 auto' : '0 0 40%'),
      width: fullscreen ? 0 : undefined,
      minWidth: 0,
      overflow: fullscreen ? 'hidden' : 'auto',
      transition: 'flex .25s, width .25s',
      background: '#fff',
      borderRight: isMobile ? 'none' : '1px solid #e5e7eb',
      borderBottom: isMobile ? '1px solid #e5e7eb' : 'none',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Wallet + Passkey section */}
          <div style={{ background: '#f0f4ff', border: '1.5px solid #c7d2fe', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: '#4338ca', marginBottom: 6 }}>
              Connected Wallet
            </div>
            {walletAddress ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
                      {walletAddress.startsWith('C') ? '🔑' : '💳'} {trunc(walletAddress)}
                    </div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>
                      {walletAddress.startsWith('C') ? 'Smart Account' : 'Stellar Wallet'}
                      {isAdmin && <span style={{ marginLeft: 6, color: '#f59e0b', fontWeight: 700 }}>· Admin</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => navigator.clipboard.writeText(walletAddress)}
                      title="Copy address"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280', padding: '2px 4px' }}
                    >📋</button>
                    <a
                      href={`https://stellar.expert/explorer/testnet/account/${walletAddress}`}
                      target="_blank" rel="noopener noreferrer"
                      title="View on Stellar Expert"
                      style={{ color: '#2563eb', fontSize: 13, textDecoration: 'none', padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                    >🔗</a>
                  </div>
                </div>
                {stellarBalance != null && (
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1d4ed8', marginTop: 6 }}>
                    {parseFloat(stellarBalance).toFixed(4)} <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}>XLM</span>
                  </div>
                )}
                {/* PasskeyKitPanel — same as main map overlay */}
                {process.env.REACT_APP_ACCOUNT_FACTORY_ID && (
                  <PasskeyKitPanel
                    walletAddress={walletAddress}
                    wallet={wallet}
                    onWalletBalanceChange={onWalletBalanceChange}
                  />
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>No wallet connected — connect from the main map</div>
            )}
          </div>

          {/* ── Vault allocation (pie chart summary) ──────────── */}
          {walletAddress && (
            <div style={{ background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: '#0284c7' }}>
                  Vault Allocation
                </div>
                <button
                  onClick={loadCountryPositions}
                  disabled={positionsLoading}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#0284c7', padding: '0 2px' }}
                  title="Refresh"
                >{positionsLoading ? '…' : '↻'}</button>
              </div>
              {totalCountryVaultXlm > 0 ? (
                <>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#0284c7', marginBottom: 2 }}>
                    {totalCountryVaultXlm.toFixed(4)}<span style={{ fontSize: 11, fontWeight: 400, color: '#64748b', marginLeft: 4 }}>XLM</span>
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 6 }}>
                    across {countryPositions.length} {countryPositions.length === 1 ? 'country' : 'countries'}
                  </div>
                  {countryPieData.length > 0 && (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={countryPieData}
                          cx="50%" cy="50%"
                          outerRadius={90}
                          dataKey="value"
                          labelLine={false}
                          label={({ name, percent }: any) =>
                            `${name} ${((percent as number) * 100).toFixed(0)}%`
                          }
                          onClick={(data: any) => flyToCountryIso2(data.name)}
                          style={{ cursor: 'pointer' }}
                        >
                          {countryPieData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <ReTooltip formatter={(v: any) => [`${Number(v).toFixed(4)} XLM`]} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </>
              ) : positionsLoading ? (
                <div style={{ fontSize: 12, color: '#64748b', textAlign: 'center', padding: '10px 0' }}>Scanning vaults…</div>
              ) : (
                <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '10px 0' }}>No vault deposits found</div>
              )}
            </div>
          )}

          {/* Quick Links removed — navigation is in the dashboard sidebar */}
        </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  const INITIAL_SHOW = 6;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', width: '100%',
      overflowX: 'hidden',
      overflowY: isMobile ? 'auto' : 'hidden',
    }}>

      {/* ── Top row: chart panel (left 40%) + map (right 60%) ── */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        flex: isMobile ? '0 0 auto' : '1 1 0',
        minHeight: 0,
        overflow: isMobile ? 'visible' : 'hidden',
      }}>
        {leftPanel}
        {/* Map wrapper — fixed height on mobile so it stays reachable when scrolling */}
        <div style={{
          flex: isMobile ? '0 0 auto' : '1 1 0',
          height: isMobile ? '60vw' : undefined,
          minHeight: isMobile ? 240 : 0,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {mapPanel}
        </div>
      </div>

      {/* ── Bottom row: country vault positions grid ── */}
      <div style={{
        flexShrink: 0,
        maxHeight: isMobile ? 'none' : '38%',
        minHeight: 80,
        borderTop: '2px solid #e5e7eb',
        background: '#f9fafb',
        overflowY: isMobile ? 'visible' : 'auto',
        padding: '12px 16px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>Country Vault Positions</div>
          {totalCountryVaultXlm > 0 && (
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0284c7' }}>{totalCountryVaultXlm.toFixed(4)} XLM</div>
          )}
          {countryPositions.length > 0 && (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              · {countryPositions.length} {countryPositions.length === 1 ? 'country' : 'countries'}
            </div>
          )}
          <button
            onClick={loadCountryPositions}
            disabled={positionsLoading}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#0284c7', padding: '2px 6px' }}
            title="Refresh positions"
          >{positionsLoading ? '…' : '↻'}</button>
        </div>

        {/* Content */}
        {!walletAddress ? (
          <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>Connect a wallet to see positions</div>
        ) : positionsLoading && countryPositions.length === 0 ? (
          <div style={{ fontSize: 12, color: '#64748b', padding: '8px 0' }}>Scanning country vaults…</div>
        ) : countryPositions.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>No vault deposits found</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
              {(showAllPositions ? countryPositions : countryPositions.slice(0, INITIAL_SHOW)).map(p => {
                const total = Number(BigInt(p.walletBal || '0') + BigInt(p.passkeyBal || '0')) / 10_000_000;
                const hasBoth = p.walletBal && p.passkeyBal;
                return (
                  <div
                    key={p.iso2}
                    onClick={() => flyToCountryIso2(p.iso2)}
                    title={`Fly to ${p.name}`}
                    style={{ background: '#fff', border: '1px solid #e0f2fe', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', transition: 'box-shadow .15s, border-color .15s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 0 0 2px #0284c7'; (e.currentTarget as HTMLDivElement).style.borderColor = '#7dd3fc'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; (e.currentTarget as HTMLDivElement).style.borderColor = '#e0f2fe'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <img
                          src={`https://flagcdn.com/24x18/${p.iso2.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/48x36/${p.iso2.toLowerCase()}.png 2x`}
                          alt={p.iso2}
                          style={{ width: 24, height: 18, objectFit: 'cover', borderRadius: 2, flexShrink: 0, display: 'block' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{p.name.split(' ').slice(0, 2).join(' ')}</div>
                          <div style={{ fontSize: 9, color: '#94a3b8', fontFamily: 'monospace' }}>{p.iso2}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0284c7', textAlign: 'right' }}>
                        {total.toFixed(4)}<span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 2 }}>XLM</span>
                      </div>
                    </div>
                    {hasBoth && (
                      <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 10, color: '#64748b' }}>
                        <span>💳 {(Number(p.walletBal) / 10_000_000).toFixed(4)}</span>
                        <span>🔑 {(Number(p.passkeyBal) / 10_000_000).toFixed(4)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {countryPositions.length > INITIAL_SHOW && (
              <button
                onClick={() => setShowAllPositions(v => !v)}
                style={{
                  marginTop: 10,
                  background: 'none',
                  border: '1px solid #bae6fd',
                  borderRadius: 6,
                  cursor: 'pointer',
                  padding: '5px 14px',
                  fontSize: 12,
                  color: '#0284c7',
                  fontWeight: 600,
                }}
              >
                {showAllPositions ? '▲ Show less' : `▼ See all ${countryPositions.length} positions`}
              </button>
            )}
          </>
        )}
      </div>

      {/* CountryProfileOverlay — opened for ALL users (same as main map).
          Admin "Manage Country" button inside it triggers onManageCountry → opens CountryManagementOverlay. */}
      {showProfile && selectedCountry && (
        <CountryProfileOverlay
          isOpen={showProfile}
          onClose={() => { setShowProfile(false); setSelectedCountry(null); }}
          countryCode={selectedCountry.code}
          countryName={selectedCountry.name}
          contractClient={contractClient}
          walletAddress={walletAddress}
          mainAdminAddress={mainAdminAddress}
          wallet={wallet}
          onDepositSuccess={handleDepositSuccess}
          onManageCountry={(country) => {
            // Escalate: close profile, open admin management overlay
            setShowProfile(false);
            setSelectedCountry(country);
            setShowAdmin(true);
          }}
        />
      )}

      {/* CountryManagementOverlay — only reachable via "Manage Country" inside CountryProfileOverlay */}
      {showAdmin && selectedCountry && (
        <CountryManagementOverlay
          isOpen={showAdmin}
          onClose={() => {
            setShowAdmin(false);
            // Return to profile overlay after closing management
            setShowProfile(true);
          }}
          countryCode={selectedCountry.code}
          countryName={selectedCountry.name}
          contractClient={contractClient}
          walletAddress={walletAddress}
          mainAdminAddress={mainAdminAddress}
          isMainAdmin={!!(walletAddress && mainAdminAddress && walletAddress === mainAdminAddress)}
          map={map.current}
          onPolicyChanged={onPolicyChanged}
          onCountryAllowedChange={onCountryAllowedChange}
          onBordersChanged={handleDashboardBordersChanged}
        />
      )}
    </div>
  );
};
